import { useMutation, useQuery, useQueryClient, type QueryClient } from "@tanstack/react-query";
import apiClient from "@/lib/api-client";
import { Sentry } from "@/lib/sentry";
import type {
  BatchUpdateResult,
  DemoteResult,
  PaperBatchResult,
  PaperCreate,
  PaperListResponse,
  PaperWithRole,
  PromoteResult,
} from "@/types/paper";

export interface UsePapersOptions {
  role?: string;
  page?: number;
  perPage?: number;
  sort?: string;
  order?: "asc" | "desc";
  minScore?: number;
  search?: string;
  /** Bulk opt-in. With shape='analytics' the backend caps at 2000;
   *  without a shape it caps at the legacy 5000. */
  mode?: "all";
  /** Slim shape — drops abstract / notes / relevance_justification.
   *  Use for visualization paths (Field Debate / Landscape / Matrix /
   *  Comparison source). Pair with `useConceptPaper` for the selected
   *  paper's heavy fields. */
  shape?: "analytics";
}

export function usePapers(conceptId: string | undefined, opts: UsePapersOptions = {}) {
  const { role, page = 1, perPage = 100, sort, order, minScore, search, mode, shape } = opts;
  return useQuery<PaperListResponse>({
    queryKey: ["papers", conceptId, role, page, perPage, sort, order, minScore, search, mode, shape],
    queryFn: async () => {
      const params = new URLSearchParams();
      if (role) params.set("role", role);
      if (mode === "all") {
        // shape=analytics → 2000-row backend cap (matches the cap the
        // backend will enforce regardless). Otherwise legacy 5000 so any
        // un-migrated call site behaves the same as before A4.
        // Cap bumped 1000 → 2000 on 2026-05-02; A3b deferred indefinitely.
        params.set("per_page", shape === "analytics" ? "2000" : "5000");
      } else {
        params.set("page", String(page));
        params.set("per_page", String(perPage));
      }
      if (sort) params.set("sort", sort);
      if (order) params.set("order", order);
      if (minScore) params.set("min_score", String(minScore));
      if (search) params.set("search", search);
      if (shape) params.set("shape", shape);
      const data: PaperListResponse = await apiClient
        .get(`api/concepts/${conceptId}/papers?${params.toString()}`)
        .json();
      if (data.truncated) {
        // First-time observability for the 2000-row analytics cap. The
        // backend won't return everything; the user sees a partial corpus
        // in Field Debate / Landscape / Matrix until we lift the cap.
        // eslint-disable-next-line no-console
        console.warn(
          `bulk_fetch_truncated: concept ${conceptId} has ${data.total_count} papers, response capped at ${data.papers.length}`,
        );
        Sentry.addBreadcrumb({
          category: "bulk_fetch_truncated",
          level: "warning",
          message: "Analytics-shape papers fetch truncated",
          data: {
            concept_id: conceptId,
            role,
            shape,
            total_count: data.total_count,
            returned: data.papers.length,
          },
        });
      }
      return data;
    },
    enabled: !!conceptId,
    placeholderData: (prev) => prev,
    // Corpus fetches are heavy (mode='all' pulls the full corpus). With the
    // default 5-minute gcTime they were evicted while the user worked on
    // Build/Analyze (zero observers there), so returning to Curate
    // cold-refetched everything behind a full loading rebuild. Keep corpora
    // warm across a work session; staleTime stays 0, so returning still
    // triggers a background refresh — only the eviction is removed.
    gcTime: 30 * 60_000,
  });
}

/**
 * Prefetch the full-shape trawled + foundational papers Expand will need on
 * mount. Called by LandscapeOverlay when it opens so navigation from
 * "Open in Expand" doesn't hit a cold cache (Analyze's analytics-shape
 * entry is keyed differently and doesn't satisfy Expand's full-shape query).
 *
 * The user typically reads the Landscape visualization for several seconds
 * before clicking "Open in Expand", so the prefetch completes before the
 * navigation. No-op on subsequent calls within React Query's staleTime.
 */
export function prefetchExpandPapers(
  queryClient: QueryClient,
  conceptId: string | undefined,
) {
  if (!conceptId) return;
  for (const role of ["trawled", "foundational"] as const) {
    // Match the queryKey shape Expand uses: page=1, perPage=100, mode='all',
    // shape=undefined, all sort/filter slots undefined.
    const queryKey = [
      "papers",
      conceptId,
      role,
      1,
      100,
      undefined,
      undefined,
      undefined,
      undefined,
      "all",
      undefined,
    ];
    queryClient.prefetchQuery<PaperListResponse>({
      queryKey,
      queryFn: async () => {
        const params = new URLSearchParams();
        params.set("role", role);
        params.set("per_page", "5000");
        return apiClient
          .get(`api/concepts/${conceptId}/papers?${params.toString()}`)
          .json<PaperListResponse>();
      },
      // Without staleTime, prefetchQuery treats every call as stale (default
      // is 0) and fires a fresh fetch on each LandscapeOverlay open.
      // 60s is enough to dedupe rapid Landscape↔Expand round-trips while
      // still letting the legit refetch path (mutate / invalidate) refresh.
      staleTime: 60_000,
    });
  }
}

/**
 * Fetch a single paper with the full PaperWithRole shape.
 *
 * A4: Analyze + Map use `shape=analytics` for the bulk list, which drops
 * abstract / notes / relevance_justification. When the user selects a
 * paper to inspect, hydrate the heavy fields on demand via this hook.
 */
export function useConceptPaper(
  conceptId: string | undefined,
  paperId: string | undefined,
) {
  return useQuery<PaperWithRole>({
    queryKey: ["concept-paper", conceptId, paperId],
    queryFn: () =>
      apiClient
        .get(`api/concepts/${conceptId}/papers/${paperId}`)
        .json<PaperWithRole>(),
    enabled: !!conceptId && !!paperId,
  });
}

export function useAddPapers(conceptId: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (data: { papers: PaperCreate[]; role?: string; added_source?: string; seed_facet_ids?: string[] }) =>
      apiClient
        .post(`api/concepts/${conceptId}/papers`, {
          json: {
            papers: data.papers,
            role: data.role ?? "foundational",
            added_source: data.added_source ?? "trawl",
            // Seeds carry their angle (facets 1c-3); ring-checked server-side.
            ...(data.seed_facet_ids ? { seed_facet_ids: data.seed_facet_ids } : {}),
          },
          timeout: 120_000,
        })
        .json<PaperBatchResult>(),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["papers", conceptId] });
      queryClient.invalidateQueries({ queryKey: ["paper-keys", conceptId] });
      queryClient.invalidateQueries({ queryKey: ["concepts"] });
    },
  });
}

export function useRemovePaper(conceptId: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (paperId: string) =>
      apiClient.delete(`api/concepts/${conceptId}/papers/${paperId}`),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["papers", conceptId] });
      queryClient.invalidateQueries({ queryKey: ["concepts"] });
    },
  });
}

export function useUpdatePaper(conceptId: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (data: { paperId: string; update: Partial<PaperWithRole> }) =>
      apiClient
        .patch(`api/concepts/${conceptId}/papers/${data.paperId}`, {
          json: data.update,
        })
        .json<PaperWithRole>(),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["papers", conceptId] });
    },
  });
}

export function usePromotePapers(conceptId: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (paperIds: string[]) =>
      apiClient
        .post(`api/concepts/${conceptId}/papers/promote`, {
          json: { paper_ids: paperIds },
        })
        .json<PromoteResult>(),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["papers", conceptId] });
      queryClient.invalidateQueries({ queryKey: ["concepts"] });
    },
  });
}

/** Seeds back to trawled — setup's untick of a library paper it promoted. */
export function useDemotePapers(conceptId: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (paperIds: string[]) =>
      apiClient
        .post(`api/concepts/${conceptId}/papers/demote`, {
          json: { paper_ids: paperIds },
        })
        .json<DemoteResult>(),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["papers", conceptId] });
      queryClient.invalidateQueries({ queryKey: ["concepts"] });
    },
  });
}

export function useBatchUpdatePapers(conceptId: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (data: { paper_ids: string[]; update: Partial<PaperWithRole> }) =>
      apiClient
        .patch(`api/concepts/${conceptId}/papers/batch`, {
          json: data,
        })
        .json<BatchUpdateResult>(),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["papers", conceptId] });
    },
  });
}

export function useSetChatSelection(conceptId: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (paperIds: string[]) =>
      apiClient
        .post(`api/concepts/${conceptId}/papers/set-chat-selection`, {
          json: { paper_ids: paperIds },
        })
        .json<BatchUpdateResult>(),
    onMutate: async (paperIds) => {
      await queryClient.cancelQueries({ queryKey: ["papers", conceptId] });
      const selectedSet = new Set(paperIds);
      // Optimistically update all papers caches for this concept
      queryClient.setQueriesData<PaperListResponse>(
        { queryKey: ["papers", conceptId] },
        (old) => {
          if (!old) return old;
          return {
            ...old,
            papers: old.papers.map((p) => ({
              ...p,
              include_in_chat: selectedSet.has(p.id),
            })),
          };
        },
      );
    },
    onSettled: () => {
      queryClient.invalidateQueries({ queryKey: ["papers", conceptId] });
    },
  });
}

export function useScoreOverride(conceptId: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (data: { paperId: string; score: number }) =>
      apiClient
        .patch(
          `api/concepts/${conceptId}/papers/${data.paperId}/score-override`,
          { json: { score: data.score === 0 ? null : data.score } },
        )
        .json<PaperWithRole>(),
    onMutate: async (data) => {
      // Optimistic update — apply immediately so thumbs/keyboard shortcuts feel instant
      const optimisticOverride = data.score === 0 ? null : data.score;
      queryClient.setQueriesData<PaperListResponse>(
        { queryKey: ["papers", conceptId] },
        (old) => {
          if (!old) return old;
          return {
            ...old,
            papers: old.papers.map((p) =>
              p.id === data.paperId
                ? { ...p, user_score_override: optimisticOverride, override_timestamp: new Date().toISOString() }
                : p,
            ),
          };
        },
      );
    },
    onSuccess: (updated) => {
      // Reconcile with server response
      queryClient.setQueriesData<PaperListResponse>(
        { queryKey: ["papers", conceptId] },
        (old) => {
          if (!old) return old;
          return {
            ...old,
            papers: old.papers.map((p) =>
              p.id === updated.id ? { ...p, user_score_override: updated.user_score_override, override_timestamp: updated.override_timestamp } : p,
            ),
          };
        },
      );
    },
    onError: () => {
      // Revert on failure by refetching
      queryClient.invalidateQueries({ queryKey: ["papers", conceptId] });
    },
  });
}

